import { useEffect, useMemo, useState } from "react";
import { api, API, fmtMoney, fmtDate, today, daysAgo } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileText, Loader2 } from "lucide-react";

const PRESETS = [
    { id: "30d", label: "Last 30 days", from: () => daysAgo(30), to: () => today() },
    { id: "90d", label: "Last 90 days", from: () => daysAgo(90), to: () => today() },
    { id: "fy", label: "This FY (Apr–Mar)", from: () => { const d = new Date(); const y = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear(); return `${y}-04-01`; }, to: () => today() },
    { id: "all", label: "Last 365 days", from: () => daysAgo(365), to: () => today() },
];

export default function CustomerStatementDialog({ open, onClose, customer }) {
    const [from, setFrom] = useState(daysAgo(90));
    const [to, setTo] = useState(today());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const ccy = data?.currency || customer?.currency || "INR";

    const load = async () => {
        if (!customer?.id) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/customers/${customer.id}/statement`, { params: { from, to } });
            setData(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, customer?.id, from, to]);

    const downloadPdf = async () => {
        const token = localStorage.getItem("vyastra_token");
        const url = `${API}/customers/${customer.id}/statement/pdf?from=${from}&to=${to}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `Statement_${(customer?.name || "customer").replace(/\s+/g, "_")}_${from}_${to}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(a.href);
    };

    const previewPdf = () => {
        // Open window SYNCHRONOUSLY inside the click handler so browsers don't block as popup
        const win = window.open("", "_blank");
        const token = localStorage.getItem("vyastra_token");
        const url = `${API}/customers/${customer.id}/statement/pdf?from=${from}&to=${to}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const blobUrl = URL.createObjectURL(b);
                if (win && !win.closed) {
                    win.location.href = blobUrl;
                } else {
                    // Popup blocked — fall back to download
                    const a = document.createElement("a");
                    a.href = blobUrl; a.target = "_blank"; a.rel = "noopener";
                    document.body.appendChild(a); a.click(); a.remove();
                }
            })
            .catch(() => { if (win) win.close(); });
    };

    const totalAging = useMemo(() => {
        if (!data?.aging) return 0;
        return Object.values(data.aging).reduce((s, v) => s + Number(v || 0), 0);
    }, [data]);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl" data-testid="customer-statement-dialog">
                <DialogHeader>
                    <div className="text-[10px] font-semibold tracking-[0.18em] text-[#a68a56]">STATEMENT OF ACCOUNT</div>
                    <DialogTitle className="text-2xl">{customer?.name}</DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        {customer?.gstin ? `GSTIN ${customer.gstin} · ` : ""}
                        Currency {ccy} · Net {customer?.payment_terms_days || 30} days
                    </p>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Range picker */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end border border-border rounded-md p-3 bg-[#fafaf7]">
                        <div>
                            <Label className="text-xs">From</Label>
                            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="statement-from" />
                        </div>
                        <div>
                            <Label className="text-xs">To</Label>
                            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="statement-to" />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => { setFrom(p.from()); setTo(p.to()); }}
                                    className="text-[11px] px-2.5 py-1 rounded-full border border-border hover:bg-white transition"
                                    data-testid={`statement-preset-${p.id}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Summary */}
                    {loading ? (
                        <div className="flex items-center gap-2 text-muted-foreground p-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading statement…
                        </div>
                    ) : data ? (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <SummaryCard label="Opening" value={fmtMoney(data.opening_balance, ccy)} />
                                <SummaryCard label="Invoiced" value={fmtMoney(data.period_debits, ccy)} />
                                <SummaryCard label="Paid" value={fmtMoney(data.period_credits, ccy)} accent="positive" />
                                <SummaryCard label="Closing" value={fmtMoney(data.closing_balance, ccy)} accent={data.closing_balance > 0.01 ? "danger" : "positive"} />
                            </div>

                            {/* Ledger */}
                            <div className="border border-border rounded-md overflow-hidden max-h-[280px] overflow-y-auto overflow-x-auto">
                                <table className="fin-table text-[12.5px] w-full">
                                    <thead className="sticky top-0 z-10">
                                        <tr><th>Date</th><th>Ref</th><th>Description</th><th className="num whitespace-nowrap">Debit</th><th className="num whitespace-nowrap">Credit</th></tr>
                                    </thead>
                                    <tbody>
                                        <tr className="bg-[#f9fafb]">
                                            <td colSpan={4} className="italic text-muted-foreground">Opening balance</td>
                                            <td className="num whitespace-nowrap tabular-nums">{fmtMoney(data.opening_balance, ccy)}</td>
                                        </tr>
                                        {data.ledger.map((r, i) => (
                                            <tr key={i} data-testid={`ledger-row-${i}`}>
                                                <td className="whitespace-nowrap">{fmtDate(r.date)}</td>
                                                <td className="font-mono text-[11.5px] whitespace-nowrap">{r.ref}</td>
                                                <td className="break-words">{r.description}</td>
                                                <td className="num whitespace-nowrap tabular-nums">{r.debit ? fmtMoney(r.debit, ccy) : "—"}</td>
                                                <td className="num whitespace-nowrap tabular-nums">{r.credit ? fmtMoney(r.credit, ccy) : "—"}</td>
                                            </tr>
                                        ))}
                                        {data.ledger.length === 0 && (
                                            <tr><td colSpan={5} className="text-center text-muted-foreground py-6">No transactions in this period.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Aging */}
                            <div className="border border-border rounded-md p-3">
                                <div className="text-[10.5px] font-semibold tracking-[0.16em] text-muted-foreground mb-2">AGING SUMMARY (AS OF {fmtDate(data.to_date).toUpperCase()})</div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                    {[
                                        ["0–30", data.aging["0-30"], "#1f6c47"],
                                        ["31–60", data.aging["31-60"], "#a68a56"],
                                        ["61–90", data.aging["61-90"], "#c97a3a"],
                                        ["90+", data.aging["90+"], "#b4233a"],
                                    ].map(([k, v, color]) => (
                                        <div key={k} className="rounded-md border border-border p-2.5 min-w-0">
                                            <div className="text-[10.5px] tracking-wide" style={{ color }}>{k} DAYS</div>
                                            <div className="mt-0.5 tabular-nums font-mono text-[12.5px] font-semibold whitespace-nowrap" data-testid={`aging-${k}`}>{fmtMoney(v, ccy)}</div>
                                        </div>
                                    ))}
                                    <div className="rounded-md border border-border p-2.5 bg-[#13192e] text-white min-w-0">
                                        <div className="text-[10.5px] tracking-wide opacity-70">TOTAL OUTSTANDING</div>
                                        <div className="mt-0.5 tabular-nums font-mono text-[12.5px] font-semibold whitespace-nowrap" data-testid="aging-total">{fmtMoney(totalAging, ccy)}</div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={previewPdf} disabled={!data} data-testid="statement-preview-pdf">
                        <FileText className="w-4 h-4 mr-1.5" /> Preview PDF
                    </Button>
                    <Button onClick={downloadPdf} disabled={!data} data-testid="statement-download-pdf">
                        <Download className="w-4 h-4 mr-1.5" /> Download PDF
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SummaryCard({ label, value, accent }) {
    const color = accent === "positive" ? "text-[#1f6c47]" : accent === "danger" ? "text-[#b4233a]" : "text-[#13192e]";
    return (
        <div className="border border-border rounded-md p-3 bg-card min-w-0">
            <div className="text-[10.5px] tracking-[0.14em] font-semibold text-muted-foreground">{label.toUpperCase()}</div>
            <div className={`mt-1 tabular-nums font-mono text-[12.5px] font-semibold whitespace-nowrap ${color}`} data-testid={`summary-${label.toLowerCase()}`}>{value}</div>
        </div>
    );
}
